import { apiClient } from '@/api/client';
import type {
  DocumentChecklistRow,
  PaginatedStudentAffairs,
  StudentAffairsResponse,
  StudentStatus,
  TransferStatus,
  UpdateDocumentRequest,
  UpdateStudentAffairsRequest,
} from '@/types/shared';

export interface ListStudentAffairsParams {
  page?: number;
  pageSize?: number;
  /** `field:ASC` / `field:DESC` — allowed fields per student-affairs.service.ts `applySort`:
   * `createdAt`, `studentStatus`, `studentCode`, `nameAr`, `nameEn`. */
  sort?: string;
  search?: string;
  studentStatus?: StudentStatus;
  /** T184 (§21) — backend `?transferStatus=` filter (T183), mirrors `studentStatus` above. */
  transferStatus?: TransferStatus;
  /** T175/T166 (§20, openapi `BranchFilter`). Narrows WITHIN the caller's resolved branch scope
   * only — never widens it. Omitted by the UI entirely for a single-branch caller (T159 §5.2). */
  branch?: number;
}

/** GET /student-affairs — Roles: Admin, Student Affairs Officer, Admissions Officer, Accounts
 * Officer, Exam Coordinator. */
export async function listStudentAffairs(
  params: ListStudentAffairsParams,
): Promise<PaginatedStudentAffairs> {
  const { data } = await apiClient.get<PaginatedStudentAffairs>('/student-affairs', { params });
  return data;
}

/** GET /student-affairs/:studentCode */
export async function getStudentAffairs(studentCode: string): Promise<StudentAffairsResponse> {
  const { data } = await apiClient.get<StudentAffairsResponse>(
    `/student-affairs/${encodeURIComponent(studentCode)}`,
  );
  return data;
}

/** PATCH /student-affairs/:studentCode — Roles: Admin, Student Affairs Officer. No `receivedBy`
 * in the payload — the server auto-stamps it whenever receipt metadata is set/changed. */
export async function updateStudentAffairsFile(
  studentCode: string,
  payload: UpdateStudentAffairsRequest,
): Promise<StudentAffairsResponse> {
  const { data } = await apiClient.patch<StudentAffairsResponse>(
    `/student-affairs/${encodeURIComponent(studentCode)}`,
    payload,
  );
  return data;
}

/** PATCH /student-affairs/:studentCode/documents/:documentType — Roles: Admin, Student Affairs
 * Officer. Server-enforced rules mirrored client-side for UX (see `lib/documentStatus.ts`):
 * `copiesCount >= 1` when `receivedStatus` is 'Received'; `notes` required when 'Received —
 * Needs Correction'; reverting to 'Not Received' resets copies to 0 and clears the received date. */
export async function updateStudentAffairsDocument(
  studentCode: string,
  documentType: string,
  payload: UpdateDocumentRequest,
): Promise<DocumentChecklistRow> {
  const { data } = await apiClient.patch<DocumentChecklistRow>(
    `/student-affairs/${encodeURIComponent(studentCode)}/documents/${encodeURIComponent(documentType)}`,
    payload,
  );
  return data;
}
